import { Component, Input, OnDestroy } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { hubConst } from '@app/core';
import { NotificationService } from '@app/core/service';
import { notNull, safeEmpty, Swal } from '@app/utils';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { OpenaiService } from '@services/openai.service';
import { MagicTextTypeEnum } from 'enums/magic-text-type.enum';
import { BehaviorSubject, Subject } from 'rxjs';
import { finalize, take, takeUntil, tap } from 'rxjs/operators';

interface ResultData {
    text: string;
    type: MagicTextTypeEnum;
    order: number;
    type_legend: string;
}

@Component({
    selector: 'gpe-modal-suggestions-ia',
    templateUrl: './modal-suggestions-ia.component.html', 
})
export class ModalSuggestionsIaComponent implements OnDestroy {
    @Input() data: any
    public readonly magicTextTypeEnum = MagicTextTypeEnum;
    public readonly resourceEditor = `${hubConst.api.avaliacoes_api}upload/ckeditor-image`;
    public loading$ = new BehaviorSubject<boolean>(false);
    public activeResultData$ = new BehaviorSubject<ResultData | undefined>(undefined);
    public textEditorCtrl = new FormControl(undefined, [Validators.required]);
    public reference: NgbModalRef;
    private destroy$ = new Subject<void>();
    private _resultData: ResultData[] = [];

    private _magicTextTypes = {
        [MagicTextTypeEnum.Rewrite]: 'ia.tipos.reescrever',
        [MagicTextTypeEnum.Shorten]: 'ia.tipos.encurtar',
        [MagicTextTypeEnum.Revise]: 'ia.tipos.corrigir-ortografia',
        [MagicTextTypeEnum.Transcript]: 'ia.tipos.transcrever',
    };

    constructor(
        private _translate: TranslateService,
        private _openAIService: OpenaiService,
        private _notificationService: NotificationService,
        private _swal: Swal,
    ) {
        this.loading$.pipe(takeUntil(this.destroy$), notNull()).subscribe(() => this.activeResultData$.next(undefined));
        this.activeResultData$
            .pipe(takeUntil(this.destroy$), notNull())
            .subscribe((resultData) => this.textEditorCtrl.setValue(resultData.text, { emitEvent: false }));

        this.textEditorCtrl.valueChanges.subscribe(
            (value) =>
            (this._resultData = this._resultData.map((item) =>
                item.order === this.activeResultData$.value?.order ? { ...item, text: value } : item,
            )),
        );
    }

    public ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    public get total(): number {
        return this._resultData?.length;
    }

    public get isImage(): boolean {
        return !!this.data.url;
    }

    public onClose() {
        if (!!this.activeResultData$.value) {
            this._swal.confirmAlertCustom(
                this._translate.instant('geral.atencao'),
                this._translate.instant('ia.deseja-realmente-fechar'),
                'info',
                this._translate.instant('geral.sim'),
                this._translate.instant('geral.cancelar'),
                { callback: () => this.reference.close() },
            )
        } else {
            this.reference.close();
        }
    }

    public onConfirm() {
        if (this.textEditorCtrl.invalid) {
            return;
        }

        this.reference.close(this.isImage ? this.textEditorCtrl.value.concat(this.data.text) : this.textEditorCtrl.value);
    }

    public onMagicText(type: number) {
        this.loading$.next(true);
        const observable = this.isImage
            ? this._openAIService.magicImage({ type, url: this.data.url })
            : this._openAIService.magicText({ type, text: this.data.text });

        observable
            .pipe(
                take(1),
                safeEmpty(),
                finalize(() => this.loading$.next(false)),
                tap({
                    error: () => {
                        this._notificationService.error('ia.ocorreu-um-problema');
                    },
                }),
            )
            .subscribe(this.addResult);
    }

    public onPrior() {
        this.setResultDataActiveByOrder(this.activeResultData$.value.order - 1);
    }

    public onNext() {
        this.setResultDataActiveByOrder(this.activeResultData$.value.order + 1);
    }

    public onPrevent(event: MouseEvent) {
        event.stopPropagation();
    }

    private setResultDataActiveByOrder(order: number) {
        const newActiveResultData = this._resultData.find((item) => item.order === order);
        if (newActiveResultData) {
            this.activeResultData$.next(newActiveResultData);
        }
    }

    private addResult = (data: any) => {
        const newData = {
            ...data,
            text: data.text,
            order: this._resultData.length + 1,
            type_legend: this._magicTextTypes[data.type],
        };

        this._resultData = [...this._resultData, newData];
        this.activeResultData$.next(newData);
    };

}
